import React from 'react';
import styled, { css } from 'styled-components';
import { FormattedMessage } from 'i18n';
import * as summariesTypes from 'state/checkoutPage/travelSummary/constants/summariesTypes';
import CloseButton from 'components/buttons/closeButton/CloseButtonComponent';
import HotelsSummaries from './components/summaries/hotel/HotelsSummaries';
import FlightsSummaries from './components/summaries/flights/FlightsSummaries';
import FlightsExtrasSummary from './components/summaries/flightsExtras/FlightsExtrasSummary';
import OtherExtrasSummary from './components/summaries/otherExtras/OtherExtrasSummary';
import PackagePrice from './components/packagePrice/PackagePrice';
import SummaryButton from './components/summaryButton/SummaryButton';
import Promotions from './components/promotions/Promotions';
import SaveForLater from './components/saveForLater/SaveForLater';
import PaymentOptionsLogos from './components/paymentOptionsLogos/PaymentOptionsLogos';
import { mediaQueries } from 'styles/utils/mediaQueries';
import flexbox from 'styles/utils/flexbox';

function TravelSummaryComponent({
  headerHeight,
  config,
  onClickClose,
  isHotelPage,
  packagePriceChanged,
  disclaimer,
  refInView,
}) {
  return (
    <Root
      headerHeight={headerHeight}
      ref={(ref) => {
        if (refInView) {
          refInView(ref);
        }
      }}
    >
      <SummaryButton {...config.button} />
      <Content {...config.content}>
        <Header>
          <DesktopTitle>
            <FormattedMessage
              id={'yourTrip.label'}
              description={'Your trip'}
              defaultMessage={'Your trip'}
              tagName={'h1'}
            />
            <FormattedMessage
              id={'bookingValidFor.label'}
              description={'Booking is only valid for 20 minutes'}
              defaultMessage={'Booking is only valid for 20 minutes'}
              tagName={'span'}
            />
          </DesktopTitle>
          <MobileTitle>
            <FormattedMessage
              id={'travelSummary.label'}
              description={'Travel Summary'}
              defaultMessage={'Travel Summary'}
              tagName={'h1'}
            />
            <CloseButton onClick={onClickClose} styles={{ padding: '0 8px' }} />
          </MobileTitle>
          <SaveForLater />
        </Header>
        <HotelsSummaries config={config.summaries.hotel} summaryType={summariesTypes.HOTEL} />
        <FlightsSummaries summaryType={summariesTypes.FLIGHT} />
        <FlightsExtrasSummary summaryType={summariesTypes.FLIGHT_EXTRAS} />
        <OtherExtrasSummary summaryType={summariesTypes.OTHER_EXTRAS} />
        <Footer>
          {packagePriceChanged && (
            <PriceChangeMessage>
              <FormattedMessage
                id={'packagePriceChanged.message'}
                description={
                  'The price has changed! This is because the selected flight or room category has been fully booked.'
                }
                defaultMessage={
                  'The price has changed! This is because the selected flight or room category has been fully booked.'
                }
              />
            </PriceChangeMessage>
          )}
          <PackagePrice />
          {!isHotelPage && <PaymentOptionsLogos />}
        </Footer>
      </Content>
      {disclaimer && (
        <DisclaimerWrap>
          <FormattedMessage
            id={'disclaimer.message'}
            description={'The trip is handled by TripX'}
            defaultMessage={'The trip is handled by TripX'}
          />
        </DisclaimerWrap>
      )}
      <Promotions {...config.promotions} paymentOptionsLogos={isHotelPage} />
    </Root>
  );
}

export default TravelSummaryComponent;

const Root = styled.div`
  user-select: none;
  display: flex;
  flex-direction: column;

  ${mediaQueries.fromAndAbove.desktop`
    width: 100%;
    position: sticky;
    top: ${({ headerHeight }) => `${headerHeight + 12}px`};
  `}
`;

const Content = styled.div`
  border: 1px solid
    ${({ theme }) => (theme.colors.border.default ? theme.colors.border.default : theme.colors.background.grey)};

  > *:not(:last-child) {
    border-bottom: 1px solid
      ${({ theme }) => (theme.colors.border.default ? theme.colors.border.default : theme.colors.background.grey)};
  }

  ${({ styles, theme }) =>
    styles && typeof styles === 'function'
      ? css`
          ${styles(theme)}
        `
      : css`
          ${styles}
        `}
`;

const Header = styled.div`
  padding: 20px;
  background-color: ${({ theme }) => theme.colors.background.light};

  h1 {
    margin-bottom: 12px;
    font-family: ${({ theme }) => theme.fonts.displaySubtle};
    font-size: 2.8em;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.text.default};
  }

  span {
    font-size: 1.2em;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.text.default};
  }
`;

const DesktopTitle = styled.div`
  display: none;

  ${mediaQueries.fromAndAbove.desktop`
    display: block;
  `}
`;

const MobileTitle = styled.div`
  ${flexbox({
    justifyContent: 'space-between',
  })}

  h1 {
    margin-bottom: 0;
    font-size: 2em;
  }

  ${mediaQueries.fromAndAbove.desktop`
    display: none;
  `}
`;

const Footer = styled.div`
  ${flexbox({
    flexDirection: 'column',
    alignItems: 'center',
  })};
  padding: 20px;
  background-color: ${({ theme }) => theme.colors.background.light};

  > *:not(:last-child) {
    margin-bottom: 16px;
  }
`;
const PriceChangeMessage = styled.div`
  font-size: 1rem;
  text-align: center;
`;

const DisclaimerWrap = styled.div`
  text-align: center;
  font-size: 12px;
  padding: 3px 0px;
  background-color: ${({ theme }) => theme.colors.background.light};
  font-style: italic;
`;
